package com.kaihuoy.home.screens

import androidx.compose.runtime.*
import com.kaihuoy.shared.data.InspectionRecord
import com.kaihuoy.shared.data.Machine
import com.kaihuoy.shared.data.ScheduledMaintenance
import com.kaihuoy.shared.data.ServiceRecord
import com.kaihuoy.shared.data.User
import com.kaihuoy.web.FirebaseService
import com.kaihuoy.components.common.*
import com.kaihuoy.components.common.*
import com.kaihuoy.web.router.AppRouterState
import kotlinx.coroutines.launch
import kotlinx.coroutines.flow.first
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date

private fun machineNoticeTimestamp(item: ScheduledMaintenance): Long =
    if (item.scheduledDate > 0) item.scheduledDate else item.timestamp

private fun formatMachineMaintenanceDate(timestamp: Long): String {
    if (timestamp <= 0) return "-"
    val date = Date(timestamp.toDouble())
    return "${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}"
}

private fun combinedAxleAndHubHours(axleHours: Long, hubHours: Long): Long {
    val knownHours = listOf(axleHours, hubHours).filter { it > 0 }
    return knownHours.minOrNull() ?: 0L
}

private data class InspectionIssue(
    val inspectionId: String,
    val inspectionTimestamp: Long,
    val inspectionUserName: String,
    val issueKey: String,
    val issueStatus: String,
    val repairNotes: String,
    val operatingHours: Long
)

private fun machineMatchesInspection(machine: Machine, inspection: InspectionRecord): Boolean {
    return machine.machineNumber.equals(inspection.serialNumber, ignoreCase = true) ||
        (machine.serialNumber.isNotBlank() && machine.serialNumber.equals(inspection.serialNumber, ignoreCase = true))
}

private fun extractOpenInspectionIssues(machine: Machine, inspections: List<InspectionRecord>): List<InspectionIssue> {
    return inspections
        .filter { machineMatchesInspection(machine, it) }
        .flatMap { inspection ->
            inspection.checklist
                .filter { (_, status) -> status == "Korjattava" || status == "Huom." }
                .filterKeys { it !in inspection.resolvedChecklistItems }
                .map { (issueKey, issueStatus) ->
                    InspectionIssue(
                        inspectionId = inspection.id,
                        inspectionTimestamp = inspection.timestamp,
                        inspectionUserName = inspection.userName,
                        issueKey = issueKey,
                        issueStatus = issueStatus,
                        repairNotes = inspection.repairNotes,
                        operatingHours = inspection.operatingHours.toLongOrNull() ?: 0L
                    )
                }
        }
        .sortedWith(compareByDescending<InspectionIssue> { it.inspectionTimestamp }.thenBy { it.issueKey })
}

private suspend fun fetchMachineMaintenanceMachines(): List<Machine> {
    val machineSnapshot = FirebaseService.database.reference("machines").valueEvents.first()
    return machineSnapshot.children.mapNotNull { child ->
        try {
            val machine = child.value<Machine>()
            if (machine.id.isBlank()) machine.copy(id = child.key ?: "") else machine
        } catch (e: Exception) {
            null
        }
    }.sortedBy { it.machineNumber }
}

private suspend fun fetchMachineMaintenanceServices(): List<ServiceRecord> {
    val serviceSnapshot = FirebaseService.database.reference("services").valueEvents.first()
    return serviceSnapshot.children.mapNotNull {
        try {
            it.value<ServiceRecord>()
        } catch (e: Exception) {
            null
        }
    }
}

private suspend fun fetchMachineMaintenanceNotices(): List<ScheduledMaintenance> {
    val noticeSnapshot = FirebaseService.database.reference("scheduled_maintenance").valueEvents.first()
    return noticeSnapshot.children.mapNotNull { child ->
        try {
            val notice = child.value<ScheduledMaintenance>()
            if (notice.id.isBlank()) notice.copy(id = child.key ?: "") else notice
        } catch (e: Exception) {
            null
        }
    }.sortedByDescending(::machineNoticeTimestamp)
}

private suspend fun fetchMachineMaintenanceInspections(userId: String, isAdmin: Boolean): List<InspectionRecord> {
    val query = if (isAdmin) {
        FirebaseService.database.reference("inspections")
    } else {
        FirebaseService.database.reference("inspections").orderByChild("userId").equalTo(userId)
    }

    val inspectionSnapshot = query.valueEvents.first()
    return inspectionSnapshot.children.mapNotNull { child ->
        try {
            val inspection = child.value<InspectionRecord>()
            if (inspection.id.isBlank()) inspection.copy(id = child.key ?: "") else inspection
        } catch (e: Exception) {
            null
        }
    }.sortedByDescending { it.timestamp }
}

private suspend fun acknowledgeInspectionIssue(
    issue: InspectionIssue,
    inspections: List<InspectionRecord>
): List<InspectionRecord> {
    val inspection = inspections.firstOrNull { it.id == issue.inspectionId } ?: return inspections
    val updatedInspection = inspection.copy(
        resolvedChecklistItems = (inspection.resolvedChecklistItems + issue.issueKey).distinct()
    )

    FirebaseService.database.reference("inspections").child(updatedInspection.id).setValue(updatedInspection)

    return inspections.map {
        if (it.id == updatedInspection.id) updatedInspection else it
    }
}

@Composable
fun MachineMaintenanceScreen(user: User?, isAdmin: Boolean) {
    var machines by remember { mutableStateOf<List<Machine>>(emptyList()) }
    var allServices by remember { mutableStateOf<List<ServiceRecord>>(emptyList()) }
    var allScheduled by remember { mutableStateOf<List<ScheduledMaintenance>>(emptyList()) }
    var allInspections by remember { mutableStateOf<List<InspectionRecord>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var selectedMachineId by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(user?.uid, isAdmin) {
        val uid = user?.uid ?: ""
        try {
            machines = fetchMachineMaintenanceMachines()
            if (uid.isBlank() && !isAdmin) {
                allServices = emptyList()
                allScheduled = emptyList()
                allInspections = emptyList()
            } else {
                allServices = fetchMachineMaintenanceServices()
                allScheduled = fetchMachineMaintenanceNotices()
                allInspections = fetchMachineMaintenanceInspections(uid, isAdmin)
            }
        } catch (e: Exception) {
            println("Konekohtaisten huoltojen haku epäonnistui: ${e.message}")
        } finally {
            isLoading = false
        }
    }

    Div(attrs = { style { padding(32.px, screenContentPadding()) } }) {
        Div(attrs = {
            style { marginBottom(16.px); cursor("pointer"); color(KaihuTheme.PrimaryTeal); fontWeight("600") }
            onClick { AppRouterState.navigate("huolto") }
        }) { Text("← Takaisin huoltoon") }

        H1(attrs = { style { marginBottom(8.px); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) { TIcon("truck"); Text(" Konekohtaiset huollot") }
        P(attrs = { style { marginBottom(24.px); color(KaihuTheme.TextSecondary) } }) {
            Text("Tarkastele huoltohistoriaa, huoltoilmoituksia ja öljynvaihtotarpeita koneittain")
        }

        if (isLoading) {
            Div(attrs = { style { textAlign("center"); padding(40.px); color(KaihuTheme.TextSecondary) } }) {
                Text("Ladataan...")
            }
        } else if (machines.isEmpty()) {
            Div(attrs = {
                style {
                    textAlign("center")
                    padding(40.px)
                    backgroundColor(KaihuTheme.CardBackground)
                    borderRadius(16.px)
                    property("box-shadow", KaihuTheme.ShadowSm)
                }
            }) {
                P(attrs = { style { fontSize(2.em) } }) { TIcon("truck", 2.em) }
                P(attrs = { style { color(KaihuTheme.TextSecondary) } }) { Text("Ei koneita järjestelmässä.") }
            }
        } else {
            Div(attrs = {
                style {
                    display(DisplayStyle.Grid)
                    gridTemplateColumns("repeat(auto-fill, minmax(min(280px, 100%), 1fr))")
                    gap(16.px)
                    marginBottom(24.px)
                }
            }) {
                machines.forEach { machine ->
                    val isSelected = selectedMachineId == machine.id
                    val machineServices = allServices.filter { it.equipmentId == machine.machineNumber || it.equipmentId == machine.id }
                    val machineNotices = allScheduled.filter { it.equipmentId == machine.id || it.machineNumber == machine.machineNumber }

                    MachineCard(machine, isSelected, machineServices.size, machineNotices.size) {
                        selectedMachineId = if (isSelected) null else machine.id
                    }
                }
            }

            val selectedMachine = machines.find { it.id == selectedMachineId }
            if (selectedMachine != null) {
                val machineServices = allServices.filter {
                    it.equipmentId == selectedMachine.machineNumber || it.equipmentId == selectedMachine.id
                }.sortedByDescending { it.serviceDate }
                val machineNotices = allScheduled.filter {
                    it.equipmentId == selectedMachine.id || it.machineNumber == selectedMachine.machineNumber
                }.sortedByDescending(::machineNoticeTimestamp)
                val machineInspectionIssues = extractOpenInspectionIssues(selectedMachine, allInspections)

                MachineDetailPanel(
                    machine = selectedMachine,
                    services = machineServices,
                    scheduled = machineNotices,
                    inspectionIssues = machineInspectionIssues,
                    inspections = allInspections,
                    onInspectionsUpdated = { allInspections = it }
                )
            }
        }
    }
}

@Composable
private fun MachineCard(
    machine: Machine,
    isSelected: Boolean,
    serviceCount: Int,
    noticeCount: Int,
    onClick: () -> Unit
) {
    var isHovered by remember { mutableStateOf(false) }

    Div(attrs = {
        onClick { onClick() }
        onMouseEnter { isHovered = true }
        onMouseLeave { isHovered = false }
        style {
            backgroundColor(if (isSelected) KaihuTheme.PrimaryTeal else KaihuTheme.CardBackground)
            color(if (isSelected) Color.white else KaihuTheme.TextPrimary)
            borderRadius(16.px)
            padding(20.px)
            cursor("pointer")
            property("box-shadow", if (isHovered) KaihuTheme.ShadowLg else KaihuTheme.ShadowMd)
            property("transition", "all 0.2s ease")
            if (isHovered && !isSelected) {
                property("transform", "translateY(-2px)")
            }
            if (isSelected) {
                border(2.px, LineStyle.Solid, KaihuTheme.PrimaryTeal)
            }
        }
    }) {
        H3(attrs = { style { margin(0.px, 0.px, 8.px, 0.px); fontSize(1.2.em); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px) } }) {
            TIcon("truck"); Text(" ${machine.machineNumber}")
        }
        P(attrs = { style { margin(0.px, 0.px, 4.px, 0.px); fontSize(0.95.em); opacity(0.85) } }) {
            Text("${machine.manufacturer} ${machine.model}")
        }
        if (machine.manufacturingYear.isNotBlank()) {
            P(attrs = { style { margin(0.px, 0.px, 4.px, 0.px); fontSize(0.85.em); opacity(0.7) } }) {
                Text("Vuosimalli: ${machine.manufacturingYear}")
            }
        }
        P(attrs = { style { margin(0.px); fontSize(0.85.em); opacity(0.7) } }) {
            Text("Käyttötunnit: ${machine.currentHours} h")
        }
        Div(attrs = { style { marginTop(8.px); display(DisplayStyle.Flex); gap(12.px); fontSize(0.85.em) } }) {
            Span(attrs={style{display(DisplayStyle.Flex);alignItems(AlignItems.Center);gap(4.px)}}) { TIcon("wrench"); Text(" $serviceCount huoltoa") }
            Span(attrs={style{display(DisplayStyle.Flex);alignItems(AlignItems.Center);gap(4.px)}}) { TIcon("speakerphone"); Text(" $noticeCount ilmoitusta") }
        }
    }
}

@Composable
private fun MachineDetailPanel(
    machine: Machine,
    services: List<ServiceRecord>,
    scheduled: List<ScheduledMaintenance>,
    inspectionIssues: List<InspectionIssue>,
    inspections: List<InspectionRecord>,
    onInspectionsUpdated: (List<InspectionRecord>) -> Unit
) {
    val scope = rememberCoroutineScope()

    Div(attrs = {
        style {
            backgroundColor(KaihuTheme.CardBackground)
            borderRadius(16.px)
            padding(24.px)
            property("box-shadow", KaihuTheme.ShadowMd)
        }
    }) {
        H2(attrs = { style { marginTop(0.px); marginBottom(16.px); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) {
            TIcon("truck"); Text(" ${machine.machineNumber} — ${machine.manufacturer} ${machine.model}")
        }

        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("repeat(auto-fit, minmax(220px, 1fr))")
                gap(12.px)
                marginBottom(24.px)
            }
        }) {
            OilInfoBox(
                label = "🛢️ Moottoriöljy ja suodatin",
                lastChangeHours = machine.lastEngineOilChangeHours,
                currentHours = machine.currentHours,
                warningAfterHours = 400,
                dueAfterHours = 500,
                recommendation = "500 h välein"
            )
            OilInfoBox(
                label = "Vaihteistoöljy",
                lastChangeHours = machine.lastTransmissionOilChangeHours,
                currentHours = machine.currentHours,
                warningAfterHours = 1000,
                dueAfterHours = 2000,
                recommendation = "1000–2000 h välein"
            )
            OilInfoBox(
                label = "🔩 Akseli- ja napaöljyt",
                lastChangeHours = combinedAxleAndHubHours(machine.lastAxleOilChangeHours, machine.lastHubOilChangeHours),
                currentHours = machine.currentHours,
                warningAfterHours = 1000,
                dueAfterHours = 2000,
                recommendation = "1000–2000 h välein"
            )
            OilInfoBox(
                label = "💧 Hydrauliikkaöljy",
                lastChangeHours = machine.lastHydraulicOilChangeHours,
                currentHours = machine.currentHours,
                warningAfterHours = 2000,
                dueAfterHours = 4000,
                recommendation = "2000–4000 h välein"
            )
        }

        H3(attrs = { style { marginBottom(12.px); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) { TIcon("notes"); Text(" Tarkastuksissa avoimet huomiot") }
        if (inspectionIssues.isEmpty()) {
            P(attrs = { style { color(KaihuTheme.TextSecondary); marginBottom(24.px) } }) {
                Text("Ei avoimia Korjattava- tai Huom.-merkintöjä tälle koneelle.")
            }
        } else {
            Div(attrs = { style { marginBottom(24.px) } }) {
                inspectionIssues.forEach { issue ->
                    Div(attrs = {
                        style {
                            backgroundColor(Color("#2a1e10"))
                            borderRadius(12.px)
                            padding(14.px)
                            marginBottom(10.px)
                            property("border", "1px solid ${KaihuTheme.Divider}")
                        }
                    }) {
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.SpaceBetween)
                                alignItems(AlignItems.Center)
                                gap(12.px)
                                marginBottom(8.px)
                            }
                        }) {
                            Div(attrs = { style { fontWeight("600") } }) {
                                Text(issue.issueKey)
                            }
                            StatusBadge(issue.issueStatus)
                        }

                        Div(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.85.em); marginBottom(8.px) } }) {
                            val meta = buildList {
                                add("Tarkastus: ${formatMachineMaintenanceDate(issue.inspectionTimestamp)}")
                                if (issue.inspectionUserName.isNotBlank()) add("Tekijä: ${issue.inspectionUserName}")
                                if (issue.operatingHours > 0) add("Tunnit: ${issue.operatingHours} h")
                            }
                            Text(meta.joinToString(" • "))
                        }

                        if (issue.repairNotes.isNotBlank()) {
                            Div(attrs = {
                                style {
                                    color(KaihuTheme.TextSecondary)
                                    fontSize(0.85.em)
                                    marginBottom(12.px)
                                }
                            }) {
                                Text("Lisätiedot: ${issue.repairNotes}")
                            }
                        }

                        StyledButton(
                            text = "✔ Kuittaa korjatuksi",
                            onClick = {
                                scope.launch {
                                    try {
                                        onInspectionsUpdated(acknowledgeInspectionIssue(issue, inspections))
                                    } catch (e: Exception) {
                                        println("Tarkastushuomion kuittaus epäonnistui: ${e.message}")
                                    }
                                }
                            },
                            backgroundColor = KaihuTheme.Green
                        )
                    }
                }
            }
        }

        H3(attrs = { style { marginBottom(12.px); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) { TIcon("speakerphone"); Text(" Huoltoilmoitukset") }
        if (scheduled.isEmpty()) {
            P(attrs = { style { color(KaihuTheme.TextSecondary) } }) {
                Text("Ei huoltoilmoituksia tälle koneelle.")
            }
        } else {
            scheduled.forEach { item ->
                val statusIcon = if (item.status == "completed") "circle-check" else "speakerphone"
                Div(attrs = {
                    style {
                        padding(10.px)
                        borderRadius(8.px)
                        marginBottom(6.px)
                        backgroundColor(if (item.status == "completed") Color("#12291f") else Color("#0d2328"))
                    }
                }) {
                    Div(attrs = { style { fontWeight("600"); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px) } }) {
                        TIcon(statusIcon); Text(" ${item.title} — ${formatMachineMaintenanceDate(machineNoticeTimestamp(item))}")
                    }
                    if (item.assignedTo.isNotBlank() || item.reportedHours > 0) {
                        Div(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.85.em); marginTop(4.px) } }) {
                            val details = buildList {
                                if (item.assignedTo.isNotBlank()) add("Ilmoittaja: ${item.assignedTo}")
                                if (item.reportedHours > 0) add("Tunnit: ${item.reportedHours} h")
                                if (item.completedHours > 0) add("Valmis: ${item.completedHours} h")
                            }
                            Text(details.joinToString(" • "))
                        }
                    }
                    if (item.oilChangeTypes.isNotEmpty()) {
                        Div(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.85.em); marginTop(4.px) } }) {
                            Text("Öljyt: ${item.oilChangeTypes.joinToString(", ")}")
                        }
                    }
                    if (item.description.isNotBlank()) {
                        Div(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.85.em); marginTop(4.px) } }) {
                            Text(item.description)
                        }
                    }
                }
            }
        }

        H3(attrs = { style { marginTop(24.px); marginBottom(12.px); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px) } }) { TIcon("wrench"); Text(" Huoltohistoria") }
        if (services.isEmpty()) {
            P(attrs = { style { color(KaihuTheme.TextSecondary) } }) { Text("Ei kirjattuja huoltoja tälle koneelle.") }
        } else {
            services.forEach { record ->
                val dateStr = formatMachineMaintenanceDate(record.serviceDate)
                Div(attrs = {
                    style {
                        padding(10.px)
                        borderRadius(8.px)
                        marginBottom(6.px)
                        backgroundColor(KaihuTheme.Surface)
                    }
                }) {
                    TIcon("wrench"); Text(" ${record.serviceType} — $dateStr — ${record.machineHours}h — ${record.userName}")
                }
            }
        }
    }
}

@Composable
private fun OilInfoBox(
    label: String,
    lastChangeHours: Long,
    currentHours: Long,
    warningAfterHours: Long,
    dueAfterHours: Long,
    recommendation: String
) {
    val hoursSinceChange = if (lastChangeHours > 0) maxOf(currentHours - lastChangeHours, 0) else -1L
    val bgColor = when {
        hoursSinceChange < 0 -> Color("#1e2235")
        hoursSinceChange >= dueAfterHours -> Color("#3b1a1a")
        hoursSinceChange >= warningAfterHours -> Color("#2e2610")
        else -> Color("#12291f")
    }
    val statusText = when {
        hoursSinceChange < 0 -> "Ei vaihtotietoa"
        hoursSinceChange >= dueAfterHours -> "Vaihto ajankohtainen"
        hoursSinceChange >= warningAfterHours -> "Suunnittele vaihto"
        else -> "Tilanne kunnossa"
    }

    Div(attrs = {
        style {
            backgroundColor(bgColor)
            borderRadius(10.px)
            padding(12.px)
            textAlign("center")
        }
    }) {
        Div(attrs = { style { fontWeight("600"); fontSize(0.9.em); marginBottom(4.px) } }) {
            Text(label)
        }
        Div(attrs = { style { fontSize(0.85.em); color(KaihuTheme.TextSecondary) } }) {
            if (lastChangeHours > 0) {
                Text("Vaihdettu: ${lastChangeHours} h • Nyt: ${currentHours} h")
                Br()
                Text("Edellisestä: ${hoursSinceChange} h")
                Br()
            }
            Text("Suositus: $recommendation")
            Br()
            Text(statusText)
        }
    }
}
